<template>
  <div class="tip-share-page">
    <div v-if="tip" class="tip-share-wrapper">
      <!-- Header brand strip -->
      <div class="brand">
        <img src="/img/icons/android-icon-192x192.png" class="brand-logo" alt="Betslip Tips" />
        <div>
          <div class="brand-name">Betslip Tips</div>
          <div class="brand-tag">Featured Win · Data behind the call</div>
        </div>
      </div>

      <div class="tip-share-card">
        <!-- Score / verdict strip -->
        <div class="score-strip">
          <div class="side side-home">
            <div class="team-name">{{ truncate(tip.homeTeam, 22) }}</div>
            <div class="team-label">HOME</div>
          </div>
          <div class="centre">
            <div class="score">
              <span class="g g-home">{{ finalHomeGoals }}</span>
              <span class="sep">—</span>
              <span class="g g-away">{{ finalAwayGoals }}</span>
            </div>
            <div class="status-badge">✅ WON</div>
          </div>
          <div class="side side-away">
            <div class="team-name">{{ truncate(tip.awayTeam, 22) }}</div>
            <div class="team-label">AWAY</div>
          </div>
        </div>

        <!-- Tip line -->
        <div class="tip-line">
          <div class="market">{{ tip.marketName }}{{ tip.handicap ? ' ' + tip.handicap : '' }}</div>
          <div class="odds">@ {{ tip.oddValue }}</div>
        </div>

        <!-- WHY pill -->
        <div v-if="whyText" class="why-pill">
          <span class="why-icon">🎯</span>
          <div>
            <div class="why-label">Why we called it (at {{ triggerMinute }}')</div>
            <div class="why-text">{{ whyText }}</div>
          </div>
        </div>

        <!-- Stat grid: radar + key stats side by side -->
        <div class="stat-grid">
          <div class="stat-radar" v-if="gm">
            <div class="stat-radar-title">Stat Share at trigger</div>
            <apexchart
              v-if="ready"
              type="radar"
              height="280"
              :options="radarOptions"
              :series="radarSeries"
            />
          </div>

          <div class="stat-bars" v-if="gm">
            <div v-for="s in statRows" :key="s.label" class="stat-row">
              <div class="stat-head">
                <span class="stat-label">{{ s.label }}</span>
                <span class="stat-winner" :class="s.winner === 'home' ? 'is-home' : 'is-away'">
                  {{ s.winner === 'home' ? truncate(tip.homeTeam, 12) : truncate(tip.awayTeam, 12) }}
                </span>
              </div>
              <div class="split">
                <div class="split-val split-home">{{ s.home }}{{ s.suffix || '' }}</div>
                <div class="split-track">
                  <div class="split-fill split-fill-home" :style="{ width: s.shareHome + '%' }"></div>
                  <div class="split-fill split-fill-away" :style="{ width: s.shareAway + '%' }"></div>
                </div>
                <div class="split-val split-away">{{ s.away }}{{ s.suffix || '' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer CTAs -->
        <div class="footer">
          <div class="cta">📲 t.me/betsliptips_free</div>
          <div class="dot">·</div>
          <div class="cta">📊 portal.betslip.tips</div>
        </div>
      </div>
    </div>

    <div v-else-if="loaded" class="empty">Tip not found.</div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "TipShare",
  data() {
    return { tip: null, loaded: false, ready: false };
  },
  computed: {
    gm() { return this.tip?.gameMoment; },
    finalHomeGoals() { return this.gm?.homeGoals ?? this.tip?.homeGoals ?? 0; },
    finalAwayGoals() { return this.gm?.awayGoals ?? this.tip?.awayGoals ?? 0; },
    triggerMinute() {
      const m = this.tip?.minute ?? this.gm?.minute;
      const n = parseFloat(m);
      return isNaN(n) ? "—" : Math.floor(n);
    },
    whyText() {
      const g = this.gm;
      if (!g) return null;
      const homeSOT = +g.homeOnTarget || 0, awaySOT = +g.awayOnTarget || 0;
      const homeDA = +g.homeDangerousAttacks || 0, awayDA = +g.awayDangerousAttacks || 0;
      const market = (this.tip?.marketName || "").toLowerCase();
      const dominator = (homeSOT + homeDA) >= (awaySOT + awayDA) ? this.tip?.homeTeam : this.tip?.awayTeam;
      if (market.includes("under")) {
        return `Low-scoring close-out signal — combined SOT only ${homeSOT + awaySOT}, score still ${this.gm?.homeGoals ?? 0}-${this.gm?.awayGoals ?? 0}.`;
      }
      if (market.includes("over") || market.includes("goal")) {
        return `${dominator} dominating with ${homeSOT}-${awaySOT} on target and ${homeDA}-${awayDA} dangerous attacks. The goal looked there.`;
      }
      return `${dominator} on top of the match — ${homeSOT}-${awaySOT} on target.`;
    },
    statRows() {
      const g = this.gm;
      if (!g) return [];
      const make = (label, h, a, suffix) => {
        const sum = h + a;
        let shareHome = sum > 0 ? Math.round((h / sum) * 100) : 50;
        let shareAway = sum > 0 ? 100 - shareHome : 50;
        if (sum > 0 && h > 0 && shareHome < 4) shareHome = 4;
        if (sum > 0 && a > 0 && shareAway < 4) shareAway = 4;
        const winner = h > a ? "home" : "away";
        return { label, home: h, away: a, suffix: suffix || "", shareHome, shareAway, winner };
      };
      return [
        make("Possession", +g.homePossession || 50, +g.awayPossession || 50, "%"),
        make("Shots on Target", +g.homeOnTarget || 0, +g.awayOnTarget || 0),
        make("Dangerous Attacks", +g.homeDangerousAttacks || 0, +g.awayDangerousAttacks || 0),
        make("Corners", +g.homeCorners || 0, +g.awayCorners || 0),
      ];
    },
    radarSeries() {
      const g = this.gm;
      if (!g) return [];
      const norm = (a, b) => {
        const t = a + b;
        if (t <= 0) return [50, 50];
        return [Math.round((a / t) * 100), Math.round((b / t) * 100)];
      };
      const pos = [+g.homePossession || 50, +g.awayPossession || 50];
      const sot = norm(+g.homeOnTarget || 0, +g.awayOnTarget || 0);
      const da = norm(+g.homeDangerousAttacks || 0, +g.awayDangerousAttacks || 0);
      const att = norm(+g.homeAttacks || 0, +g.awayAttacks || 0);
      const cor = norm(+g.homeCorners || 0, +g.awayCorners || 0);
      const cards = norm((+g.homeYellowCards || 0) + (+g.homeRedCards || 0), (+g.awayYellowCards || 0) + (+g.awayRedCards || 0));
      return [
        { name: this.tip?.homeTeam || "Home", data: [pos[0], sot[0], da[0], att[0], cor[0], cards[0]] },
        { name: this.tip?.awayTeam || "Away", data: [pos[1], sot[1], da[1], att[1], cor[1], cards[1]] },
      ];
    },
    radarOptions() {
      return {
        chart: { type: "radar", toolbar: { show: false }, animations: { enabled: false } },
        colors: ["#407BFF", "#FFCF75"],
        stroke: { width: 2 },
        fill: { opacity: 0.4 },
        markers: { size: 4 },
        xaxis: {
          categories: ["Poss.", "SOT", "Dang. Att", "Attacks", "Corners", "Cards"],
          labels: { style: { fontSize: "11px", colors: Array(6).fill("#fff"), fontWeight: 600 } },
        },
        yaxis: { show: false, min: 0, max: 100 },
        legend: { position: "bottom", labels: { colors: "#fff" }, fontSize: "11px" },
        plotOptions: { radar: { polygons: { strokeColors: "rgba(255,255,255,0.25)", connectorColors: "rgba(255,255,255,0.15)" } } },
      };
    },
  },
  async created() {
    try {
      const id = this.$route.params.id;
      const r = await axios.get(`/api/History/public/${id}`);
      this.tip = r.data;
    } catch (e) {
      console.error("TipShare load failed", e);
    } finally {
      this.loaded = true;
      // Small settle delay so apexcharts renders before screenshot
      setTimeout(() => { this.ready = true; }, 100);
    }
  },
  methods: {
    truncate(s, n) {
      if (!s) return "";
      return s.length > n ? s.slice(0, n - 1) + "…" : s;
    },
  },
};
</script>

<style scoped>
.tip-share-page {
  width: 1200px;
  height: 630px;
  background: linear-gradient(135deg, #07255B 0%, #0a1f4d 60%, #1d3a8a 100%);
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.tip-share-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #fff;
  padding: 0 4px;
}
.brand-logo { width: 44px; height: 44px; border-radius: 12px; }
.brand-name { font-size: 20px; font-weight: 800; line-height: 1; }
.brand-tag { font-size: 12px; opacity: 0.75; margin-top: 2px; }

.tip-share-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 22px 28px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Score strip */
.score-strip {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.side { display: flex; flex-direction: column; }
.side-home { align-items: flex-end; text-align: right; }
.side-away { align-items: flex-start; text-align: left; }
.team-name { font-size: 22px; font-weight: 700; line-height: 1.1; }
.team-label { font-size: 10px; letter-spacing: 1.5px; opacity: 0.5; margin-top: 4px; font-weight: 600; }
.centre { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.score { display: flex; gap: 12px; font-size: 44px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.g-home { color: #407BFF; }
.g-away { color: #FFCF75; }
.sep { color: rgba(255,255,255,0.35); }
.status-badge {
  background: rgba(90, 226, 156, 0.18);
  color: #5ae29c;
  border: 1px solid rgba(90, 226, 156, 0.4);
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Tip line */
.tip-line {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 14px;
  font-size: 18px;
  font-weight: 600;
}
.market { color: #fff; }
.odds { color: #FFCF75; font-weight: 800; }

/* Why pill */
.why-pill {
  background: rgba(255, 207, 117, 0.13);
  border: 1px solid rgba(255, 207, 117, 0.35);
  border-radius: 14px;
  padding: 10px 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.why-icon { font-size: 20px; line-height: 1.1; }
.why-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.3px; opacity: 0.75; margin-bottom: 2px; font-weight: 600; }
.why-text { font-weight: 600; font-size: 13px; line-height: 1.4; }

/* Stat grid */
.stat-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 18px;
  min-height: 0;
}
.stat-radar {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  padding: 10px 6px 4px;
}
.stat-radar-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  opacity: 0.65;
  margin: 0 0 4px 14px;
  font-weight: 600;
}

.stat-bars { display: flex; flex-direction: column; gap: 8px; }
.stat-row {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 8px 14px;
}
.stat-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.1px; opacity: 0.7; font-weight: 700; }
.stat-winner { font-size: 9px; padding: 2px 8px; border-radius: 8px; font-weight: 700; white-space: nowrap; }
.stat-winner.is-home { background: rgba(64, 123, 255, 0.28); color: #cfdfff; }
.stat-winner.is-away { background: rgba(255, 207, 117, 0.25); color: #ffe2b3; }

.split { display: grid; grid-template-columns: 42px 1fr 42px; align-items: center; gap: 10px; }
.split-val { font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; }
.split-home { text-align: right; color: #cfdfff; }
.split-away { text-align: left; color: #ffe2b3; }
.split-track { display: flex; height: 9px; background: rgba(255,255,255,0.06); border-radius: 5px; overflow: hidden; }
.split-fill { height: 100%; }
.split-fill-home { background: linear-gradient(90deg, #3555FE, #3EA1FC); }
.split-fill-away { background: linear-gradient(90deg, #FF9C40, #FFCF75); }

/* Footer */
.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 13px;
  font-weight: 600;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.08);
  opacity: 0.9;
}
.cta { color: #fff; }
.dot { opacity: 0.4; }

.empty {
  color: #fff;
  font-size: 18px;
  align-self: center;
  margin: auto;
}
</style>
